import React, { useState, useEffect, useContext } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { AiOutlineMenu } from 'react-icons/ai';
import { RiNotification3Line } from 'react-icons/ri';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { FiSettings } from 'react-icons/fi';
import { TooltipComponent } from '@syncfusion/ej2-react-popups';
// Assuming DropDownListComponent is used for something like time/theme selection,
// but for a sleeker look, we might abstract it or integrate it differently.
// For now, keeping it as is, but its visual integration could be part of ThemeSettings or UserProfile.
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns'; 

import { Notification, UserProfile } from '.'; // Assuming these are components in the same folder
import AppContext from '../contexts/AppContext'; // Ensure this path is correct
import { ThemeSettings } from '../components'; // Ensure this path is correct
import { dropdownData } from '../data/dummy'; // Ensure this path is correct

// --- NavButton Component (Re-styled for sleekness) ---
const NavButton = ({ title, customFunc, icon, color, dotColor }) => (
  <TooltipComponent content={title} position="BottomCenter">
    <button
      type="button"
      onClick={() => customFunc()}
      // Sleeker: smaller size, more refined hover/active states, futuristic border
      className="relative text-lg p-2 md:p-2.5 rounded-full bg-white/5 hover:bg-white/15 backdrop-blur-md shadow-lg
                 transition-all duration-300 ease-out transform hover:scale-105
                 border border-transparent hover:border-cyan-400 hover:shadow-cyan-glow
                 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-opacity-75"
    >
      {dotColor && (
        <span
          style={{ background: dotColor }}
          className="absolute inline-flex rounded-full h-2 w-2 right-1.5 top-1.5 animate-pulse-fast" // Smaller, faster pulse
        />
      )}
      <div className="text-cyan-300 transition-colors duration-300 group-hover:text-cyan-200">{icon}</div>
    </button>
  </TooltipComponent>
);

// --- DropDown Component (keeping as is for functionality, but styling needs attention) ---
// Note: Styling for DropDownListComponent is usually handled by Syncfusion's themes.
// To make it futuristic, you might need custom CSS overrides or a different component.
const DropDown = ({ currentMode }) => (
  <div className="w-28 border border-gray-700 px-2 py-1 rounded-md bg-gray-800 text-white">
    <DropDownListComponent
      id="time"
      fields={{ text: 'Time', value: 'Id' }}
      // Adjusted style for dark mode, still relying on Syncfusion's rendering
      style={{ border: 'none', background: 'transparent', color: currentMode === 'Dark' ? '#E0E0E0' : '#333' }}
      value="1"
      dataSource={dropdownData}
      popupHeight="220px"
      popupWidth="120px"
    />
  </div>
);

// --- Main Navbar Component ---
const Navbar = () => {
  const {
    themeSettings, setThemeSettings, currentMode, currentColor,
    activeMenu, setActiveMenu, handleClick, isClicked,
    setScreenSize, userData, activeSectionLinks, setSuppressAutoNavigate, suppressAutoNavigate
  } = useContext(AppContext);

  const navigate = useNavigate();
  const location = useLocation(); // ✅ Track current route
  const [clickedSection, setClickedSection] = useState(null);

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Effect to handle navigation based on activeSectionLinks
  useEffect(() => {
    if (activeSectionLinks.length === 0 || suppressAutoNavigate) return;
  
    // Navigate only when a new sidebar section is clicked and not suppressed
    const firstLink = activeSectionLinks[0]?.name;
    if (firstLink) {
      const targetPath = `/${firstLink}`;
      // Only navigate if the current path is different to avoid unnecessary re-renders/pushes
      if (location.pathname !== targetPath) {
        navigate(targetPath);
      }
    }
  }, [activeSectionLinks, suppressAutoNavigate, navigate, location.pathname]);


  // User Profile Icon with futuristic glow
  const UserProfileIcon = ({ name, profileImage }) => (
    <div className="flex items-center gap-2 cursor-pointer
                p-1.5 rounded-full bg-white/5 hover:bg-white/15 backdrop-blur-md shadow-lg
                transition-all duration-300 ease-out transform hover:scale-105
                border border-transparent hover:border-purple-400 hover:shadow-purple-glow">
      <img
        src={profileImage}
        alt="profile"
        className="w-7 h-7 md:w-8 md:h-8 rounded-full border-2 border-purple-600 shadow-md object-cover" // Slightly smaller, object-cover for better image fitting
      />
      <span className="hidden sm:inline text-sm text-white/90 font-medium tracking-wide">
        Hi, <span className="text-purple-300">{name}</span>
      </span>
      <MdKeyboardArrowDown className="text-purple-400 text-xl transition-transform duration-300 group-hover:rotate-180" />
    </div>
  );

  const handleActiveMenu = () => setActiveMenu(!activeMenu);
  const toggleThemeSettings = () => setThemeSettings(prev => !prev);

  return (
    <div className="flex justify-between items-center px-4 py-3 md:px-6 relative
                    bg-gradient-to-r from-[#0a0f24] via-[#131e38] to-[#0a0f24] // Deeper, more consistent dark gradient
                    shadow-2xl border-b border-indigo-900 backdrop-blur-xl"> {/* Stronger shadow, subtle border */}

      {/* Menu Toggle Button */}
      <NavButton
        title="Menu"
        customFunc={handleActiveMenu}
        color={currentColor} // Retaining custom color for primary actions
        icon={<AiOutlineMenu />}
      />

      {/* Dynamic Navigation Links (smaller, more stylized) */}
      <div className="flex gap-1.5 md:gap-2 items-center px-2 py-1 overflow-x-auto max-w-[calc(100%-180px)] md:max-w-[calc(100%-250px)]
                      scrollbar-thin scrollbar-thumb-purple-700/50 scrollbar-track-transparent">
        {activeSectionLinks.map((link) => (
          <NavLink
            key={link.name}
            to={`/${link.name}`}
            onClick={() => {
              setClickedSection(link.name);
              setSuppressAutoNavigate(true); // Prevent auto-redirect on navbar click
            }}
            className={({ isActive }) =>
              `flex items-center justify-center text-center whitespace-nowrap
              px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-semibold
              transition-all duration-300 ease-in-out shadow-lg transform hover:scale-105
              ${isActive
                ? 'bg-gradient-to-r from-cyan-600 to-purple-600 text-white ' +
                  'shadow-[0_0_15px_rgba(70,200,255,0.7)] border border-cyan-400' // Stronger active glow
                : 'bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white ' +
                  'hover:shadow-[0_0_8px_rgba(255,255,255,0.2)] border border-transparent' // Subtle hover glow
              }`
            }
            style={{ minWidth: 'fit-content' }}
          >
            <span className="capitalize tracking-wider">{link.name}</span>
          </NavLink>
        ))}
      </div>

      {/* Right-aligned Action Buttons */}
      <div className="flex items-center gap-1 md:gap-2"> {/* Reduced gap for sleeker look */}
        <NavButton
          title="Notification"
          dotColor="rgb(254, 201, 15)" // Standard notification dot color
          customFunc={() => handleClick('notification')}
          color={currentColor}
          icon={<RiNotification3Line />}
        />

        <NavButton
          title="Settings"
          customFunc={toggleThemeSettings}
          color={currentColor}
          icon={<FiSettings />}
        />

        {/* User Profile Button - now uses the custom component directly */}
        <TooltipComponent content="User Profile" position="BottomCenter">
          <div onClick={() => handleClick('userProfile')} className="cursor-pointer">
            <UserProfileIcon
              name={userData?.data?.employees?.first_name}
              profileImage={userData?.data?.employees?.photo_url}
            />
          </div>
        </TooltipComponent>

        {isClicked.notification && <Notification />}
        {isClicked.userProfile && <UserProfile />}
        {themeSettings && <ThemeSettings />}
      </div>
    </div>
  );
};

export default Navbar;